import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

export const languages = {
  en: { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  tr: { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', dir: 'ltr' },
  el: { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', dir: 'ltr' },
  ar: { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
};

// Translations
const translations = {
  en: {
    // Common
    signIn: 'Sign In',
    signOut: 'Sign Out',
    dashboard: 'Dashboard',
    settings: 'Settings',
    loading: 'Loading...',
    grams: 'grams',
    gram: 'g',
    reset: 'Reset',
    
    // Navigation
    whyGold: 'Why Gold',
    performance: 'Performance',
    calculator: 'Calculator',
    sources: 'Sources',
    
    // Landing Page
    heroTitle: 'With my money,',
    heroHighlight: 'what can I do?',
    heroSubtitle: 'Explore how gold performed over the last 5 years and estimate future scenarios using public market data.',
    calculateWithGold: 'Calculate with Gold',
    viewPerformance: 'View 5-Year Performance',
    publicMarketData: 'Public market data',
    transparentSources: 'Transparent sources',
    notFinancialAdvice: 'Not financial advice',
    liveMarketData: 'Live market data · Updates every 10 min',
    
    // Promo Card
    trackYourGold: 'Track Your Gold Holdings',
    promoDescription: 'Sign in to monitor your gold portfolio, view daily performance changes, and track profit over time.',
    dailyChanges: 'Daily Changes',
    
    // Dashboard
    welcomeBack: 'Welcome back,',
    hereIsPerformance: "Here's how your gold is performing",
    startTracking: 'Start tracking your gold journey today',
    currentValue: 'Current Value',
    todaysChange: "Today's Change",
    totalProfitLoss: 'Total Profit/Loss',
    goldOwned: 'Gold Owned',
    yourGoldHolding: 'Your Gold Holding',
    goldAmount: 'Gold Amount',
    trackingSince: 'Tracking since',
    daysTracked: 'Days tracked',
    startPrice: 'Start price',
    sinceStart: 'since start',
    edit: 'Edit',
    delete: 'Delete',
    perGram: 'per gram',
    
    // Empty State
    startYourGoldJourney: 'Start Your Gold Journey',
    emptyStateDescription: 'Enter how much gold you own (or want to track hypothetically) and watch how it performs over time with live market data.',
    addYourGoldHolding: 'Add Your Gold Holding',
    
    // Holding Modal
    addGoldHolding: 'Add Gold Holding',
    editGoldHolding: 'Edit Gold Holding',
    howMuchGold: 'How much gold do you own?',
    gramsOfGold: 'grams of gold',
    whenDidYouStart: 'When did you start tracking?',
    selectDate: 'Select date',
    today: 'Today',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving...',
    
    // Why Gold Section
    whyGoldTitle: 'Why Gold?',
    whyGoldSubtitle: 'Discover the timeless benefits of gold as part of a diversified portfolio.',
    inflationHedge: 'Inflation Hedge',
    inflationHedgeDesc: 'Gold has historically maintained its purchasing power during periods of rising inflation and currency devaluation.',
    globallyRecognized: 'Globally Recognized',
    globallyRecognizedDesc: 'Accepted worldwide as a store of value for thousands of years, transcending borders and governments.',
    portfolioDiversification: 'Portfolio Diversification',
    portfolioDiversificationDesc: 'Often moves independently of stocks and bonds, potentially reducing overall portfolio risk.',
    highlyLiquid: 'Highly Liquid',
    highlyLiquidDesc: 'One of the most liquid assets globally, easily bought and sold in markets around the world.',
    
    // Calculator
    goldCalculator: 'Gold Calculator',
    calculatorSubtitle: 'See how much gold you could buy today and estimate potential future values.',
    investmentCalculator: 'Investment Calculator',
    calculateYourGold: 'Calculate Your Gold Investment',
    goldPricePerGram: 'Gold Price per Gram',
    quickConverter: 'Quick Converter: How much gold can I buy?',
    enterAmount: 'Enter amount',
    enterAmountToConvert: 'Enter amount to convert',
    investmentAmount: 'Investment Amount',
    currency: 'Currency',
    timeHorizon: 'Time Horizon',
    years: 'years',
    year: 'year',
    scenario: 'Scenario',
    growthScenario: 'Growth Scenario',
    conservative: 'Conservative',
    moderate: 'Moderate',
    optimistic: 'Optimistic',
    base: 'Base',
    calculate: 'Calculate',
    results: 'Results',
    futureValue: 'Future Value',
    goldGrams: 'Gold Amount',
    totalReturn: 'Total Return',
    annualReturn: 'Annual Return',
    basedOnHistorical: 'Based on historical performance. Past results do not guarantee future returns.',
    inputYourDetails: 'Input Your Details',
    readyToCalculate: 'Ready to Calculate',
    enterDetailsToSee: 'Enter your investment details to see how much gold you could buy and estimate future values.',
    yourGoldInvestment: 'Your Gold Investment',
    todayYouCouldBuy: 'Today you could buy approximately',
    with: 'With',
    afterYears: 'After',
    estimatedRange: 'Estimated Range',
    selected: 'Selected',
    calculationDetails: 'Calculation Details',
    goldPrice: 'Gold Price',
    fxRate: 'FX Rate',
    annualGrowth: 'Annual Growth',
    currentMarketData: 'Current Market Data',
    loadingMarketData: 'Loading market data...',
    indicative: 'Indicative',
    liveRates: 'Live rates',
    updatesEvery10Min: 'Updates every 10 min',
    disclaimer: 'This tool provides illustrative scenarios based on public data. It is not financial advice. Past performance does not guarantee future returns.',
    
    // Performance Section
    goldPerformance: 'Gold Performance',
    performanceSubtitle: 'Track how gold has performed over the past 5 years with real market data.',
    fiveYearPerformance: '5-Year Performance',
    currentPrice: 'Current Price',
    change24h: '24h Change',
    change1y: '1 Year Change',
    change5y: '5 Year Change',
    historicalPerformance: 'Historical Performance',
    goldPriceEUR: 'Gold Price (EUR)',
    trackGoldPerformance: 'Track how gold has performed in euros over time.',
    start: 'Start',
    current: 'Current',
    change: 'Change',
    loadingChartData: 'Loading chart data...',
    dataTemporarilyUnavailable: 'Data Temporarily Unavailable',
    historicalDataUnavailable: 'Historical price data is currently unavailable. Please try again later.',
    source: 'Source',
    indicativeData: 'Indicative data',
    dataDelayedInfo: 'Data may be delayed and is provided for informational purposes only.',
    
    // News Section
    goldMarketNews: 'Gold Market News',
    latestNews: 'Stay updated with the latest gold market news and analysis.',
    readMore: 'Read more',
    
    // Sources Section
    dataSources: 'Data Sources',
    sourcesSubtitle: 'We use reliable, publicly available data sources for all our calculations.',
    transparency: 'Transparency',
    howWeHandleData: 'How We Handle Data',
    publicSourcesOnly: 'Public Sources Only',
    publicSourcesDesc: 'All data comes from publicly available, reputable sources.',
    regularUpdates: 'Regular Updates',
    regularUpdatesDesc: 'Prices are refreshed every 10 minutes. Data may be delayed.',
    indicativeOnly: 'Indicative Only',
    indicativeOnlyDesc: 'Prices shown are indicative and may differ from actual trading prices.',
    visitSource: 'Visit Source',
    dataDisclaimer: 'Data is provided for informational purposes only. Always verify prices before making any investment decisions.',
    
    // Login Page
    welcome: 'Welcome',
    signInWithEmail: 'Sign in with your email',
    emailAddress: 'Email address',
    sendMagicLink: 'Send Magic Link',
    sendVerificationCode: 'Send Verification Code',
    verificationCodeDescription: "We'll send you a 6-digit code to sign in. No password needed!",
    enterVerificationCode: 'Enter verification code',
    codeSentTo: 'We sent a 6-digit code to',
    verifyAndSignIn: 'Verify & Sign In',
    didntReceiveCode: "Didn't receive the code?",
    resendCode: 'Resend code',
    resendIn: 'Resend in',
    invalidCode: 'Invalid code. Please try again.',
    enterFullCode: 'Please enter all 6 digits',
    failedToSendCode: 'Failed to send code. Please try again.',
    redirectingToDashboard: 'Redirecting to your dashboard...',
    back: 'Back',
    magicLinkDescription: "We'll send you an email with a link to sign in. No password needed!",
    checkYourEmail: 'Check your email',
    magicLinkSent: 'We sent a sign-in link to',
    linkExpiresIn: 'Click the link in your email to sign in. The link expires in 30 minutes.',
    useDifferentEmail: 'Use a different email',
    backToHomepage: '← Back to homepage',
    bySigningIn: 'By signing in, you agree to our',
    termsOfService: 'Terms of Service',
    and: 'and',
    privacyPolicy: 'Privacy Policy',
    
    // Verify Page
    verifyingLink: 'Verifying your link',
    pleaseWait: 'Please wait while we sign you in...',
    youreSignedIn: "You're signed in!",
    redirectingToDashboard: 'Redirecting to your dashboard...',
    linkExpiredOrInvalid: 'Link expired or invalid',
    requestNewLink: 'Request New Link',
    
    // Footer
    footerDisclaimer: 'This is not financial advice. Gold prices are based on publicly available market data.',
    allRightsReserved: 'All rights reserved.',
    
    // Settings
    language: 'Language',
    selectLanguage: 'Select Language',
    
    // Delete confirmation
    deleteHolding: 'Delete Holding',
    deleteConfirmation: 'Are you sure you want to delete your gold holding? This action cannot be undone.',
    confirmDelete: 'Yes, Delete',
  },
  
  tr: {
    // Common
    signIn: 'Giriş Yap',
    signOut: 'Çıkış Yap',
    dashboard: 'Kontrol Paneli',
    settings: 'Ayarlar',
    loading: 'Yükleniyor...',
    grams: 'gram',
    gram: 'g',
    reset: 'Sıfırla',
    
    // Navigation
    whyGold: 'Neden Altın',
    performance: 'Performans',
    calculator: 'Hesaplayıcı',
    sources: 'Kaynaklar',
    
    // Landing Page
    heroTitle: 'Paramla,',
    heroHighlight: 'ne yapabilirim?',
    heroSubtitle: 'Son 5 yılda altının performansını keşfedin ve halka açık piyasa verilerini kullanarak gelecek senaryolarını tahmin edin.',
    calculateWithGold: 'Altınla Hesapla',
    viewPerformance: '5 Yıllık Performansı Gör',
    publicMarketData: 'Halka açık piyasa verileri',
    transparentSources: 'Şeffaf kaynaklar',
    notFinancialAdvice: 'Yatırım tavsiyesi değildir',
    liveMarketData: 'Canlı piyasa verileri · Her 10 dk güncellenir',
    
    // Promo Card
    trackYourGold: 'Altın Varlıklarınızı Takip Edin',
    promoDescription: 'Altın portföyünüzü izlemek, günlük performans değişikliklerini görmek ve kârınızı takip etmek için giriş yapın.',
    dailyChanges: 'Günlük Değişimler',
    
    // Dashboard
    welcomeBack: 'Tekrar hoş geldiniz,',
    hereIsPerformance: 'Altınınızın performansı',
    startTracking: 'Bugün altın yolculuğunuzu takip etmeye başlayın',
    currentValue: 'Mevcut Değer',
    todaysChange: 'Bugünkü Değişim',
    totalProfitLoss: 'Toplam Kâr/Zarar',
    goldOwned: 'Sahip Olunan Altın',
    yourGoldHolding: 'Altın Varlığınız',
    goldAmount: 'Altın Miktarı',
    trackingSince: 'Takip başlangıcı',
    daysTracked: 'Takip edilen gün',
    startPrice: 'Başlangıç fiyatı',
    sinceStart: 'başlangıçtan beri',
    edit: 'Düzenle',
    delete: 'Sil',
    perGram: 'gram başına',
    
    // Empty State
    startYourGoldJourney: 'Altın Yolculuğunuza Başlayın',
    emptyStateDescription: 'Sahip olduğunuz (veya varsayımsal olarak takip etmek istediğiniz) altın miktarını girin ve canlı piyasa verileriyle zaman içinde nasıl performans gösterdiğini izleyin.',
    addYourGoldHolding: 'Altın Varlığınızı Ekleyin',
    
    // Holding Modal
    addGoldHolding: 'Altın Varlığı Ekle',
    editGoldHolding: 'Altın Varlığını Düzenle',
    howMuchGold: 'Ne kadar altına sahipsiniz?',
    gramsOfGold: 'gram altın',
    whenDidYouStart: 'Takibe ne zaman başladınız?',
    selectDate: 'Tarih seçin',
    today: 'Bugün',
    save: 'Kaydet',
    cancel: 'İptal',
    saving: 'Kaydediliyor...',
    
    // Why Gold Section
    whyGoldTitle: 'Neden Altın?',
    whyGoldSubtitle: 'Çeşitlendirilmiş bir portföyün parçası olarak altının zamansız faydalarını keşfedin.',
    inflationHedge: 'Enflasyona Karşı Koruma',
    inflationHedgeDesc: 'Altın, yükselen enflasyon ve para birimi değer kaybı dönemlerinde tarihsel olarak satın alma gücünü korumuştur.',
    globallyRecognized: 'Dünya Çapında Kabul Görmüş',
    globallyRecognizedDesc: 'Binlerce yıldır sınırları ve hükümetleri aşarak dünya çapında bir değer deposu olarak kabul görmüştür.',
    portfolioDiversification: 'Portföy Çeşitlendirmesi',
    portfolioDiversificationDesc: 'Genellikle hisse senetleri ve tahvillerden bağımsız hareket eder, potansiyel olarak genel portföy riskini azaltır.',
    highlyLiquid: 'Yüksek Likidite',
    highlyLiquidDesc: 'Dünya genelinde en likit varlıklardan biri, dünya çapındaki piyasalarda kolayca alınıp satılabilir.',
    
    // Calculator
    goldCalculator: 'Altın Hesaplayıcı',
    calculatorSubtitle: 'Bugün ne kadar altın alabileceğinizi görün ve potansiyel gelecek değerlerini tahmin edin.',
    investmentCalculator: 'Yatırım Hesaplayıcı',
    calculateYourGold: 'Altın Yatırımınızı Hesaplayın',
    goldPricePerGram: 'Gram Başına Altın Fiyatı',
    quickConverter: 'Hızlı Dönüştürücü: Ne kadar altın alabilirim?',
    enterAmount: 'Miktar girin',
    enterAmountToConvert: 'Dönüştürmek için miktar girin',
    investmentAmount: 'Yatırım Tutarı',
    currency: 'Para Birimi',
    timeHorizon: 'Zaman Dilimi',
    years: 'yıl',
    year: 'yıl',
    scenario: 'Senaryo',
    growthScenario: 'Büyüme Senaryosu',
    conservative: 'Muhafazakâr',
    moderate: 'Orta',
    optimistic: 'İyimser',
    base: 'Temel',
    calculate: 'Hesapla',
    results: 'Sonuçlar',
    futureValue: 'Gelecek Değeri',
    goldGrams: 'Altın Miktarı',
    totalReturn: 'Toplam Getiri',
    annualReturn: 'Yıllık Getiri',
    basedOnHistorical: 'Geçmiş performansa dayanmaktadır. Geçmiş sonuçlar gelecekteki getirileri garanti etmez.',
    inputYourDetails: 'Bilgilerinizi Girin',
    readyToCalculate: 'Hesaplamaya Hazır',
    enterDetailsToSee: 'Yatırım detaylarınızı girerek ne kadar altın alabileceğinizi ve gelecek değerlerini tahmin edin.',
    yourGoldInvestment: 'Altın Yatırımınız',
    todayYouCouldBuy: 'Bugün yaklaşık olarak alabilirsiniz',
    with: 'İle',
    afterYears: 'Sonra',
    estimatedRange: 'Tahmini Aralık',
    selected: 'Seçili',
    calculationDetails: 'Hesaplama Detayları',
    goldPrice: 'Altın Fiyatı',
    fxRate: 'Döviz Kuru',
    annualGrowth: 'Yıllık Büyüme',
    currentMarketData: 'Güncel Piyasa Verileri',
    loadingMarketData: 'Piyasa verileri yükleniyor...',
    indicative: 'Gösterge',
    liveRates: 'Canlı kurlar',
    updatesEvery10Min: 'Her 10 dk güncellenir',
    disclaimer: 'Bu araç, halka açık verilere dayalı örnek senaryolar sunar. Yatırım tavsiyesi değildir. Geçmiş performans gelecekteki sonuçları garanti etmez.',
    
    // Performance Section
    goldPerformance: 'Altın Performansı',
    performanceSubtitle: 'Gerçek piyasa verileriyle altının son 5 yılda nasıl performans gösterdiğini takip edin.',
    fiveYearPerformance: '5 Yıllık Performans',
    currentPrice: 'Güncel Fiyat',
    change24h: '24s Değişim',
    change1y: '1 Yıllık Değişim',
    change5y: '5 Yıllık Değişim',
    historicalPerformance: 'Geçmiş Performans',
    goldPriceEUR: 'Altın Fiyatı (EUR)',
    trackGoldPerformance: 'Altının euro cinsinden zaman içindeki performansını takip edin.',
    start: 'Başlangıç',
    current: 'Güncel',
    change: 'Değişim',
    loadingChartData: 'Grafik verileri yükleniyor...',
    dataTemporarilyUnavailable: 'Veriler Geçici Olarak Kullanılamıyor',
    historicalDataUnavailable: 'Geçmiş fiyat verileri şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.',
    source: 'Kaynak',
    indicativeData: 'Gösterge verisi',
    dataDelayedInfo: 'Veriler gecikmiş olabilir ve yalnızca bilgilendirme amaçlıdır.',
    
    // News Section
    goldMarketNews: 'Altın Piyasası Haberleri',
    latestNews: 'En son altın piyasası haberleri ve analizleriyle güncel kalın.',
    readMore: 'Devamını oku',
    
    // Sources Section
    dataSources: 'Veri Kaynakları',
    sourcesSubtitle: 'Tüm hesaplamalarımız için güvenilir, halka açık veri kaynakları kullanıyoruz.',
    transparency: 'Şeffaflık',
    howWeHandleData: 'Verileri Nasıl İşliyoruz',
    publicSourcesOnly: 'Yalnızca Halka Açık Kaynaklar',
    publicSourcesDesc: 'Tüm veriler halka açık, güvenilir kaynaklardan gelmektedir.',
    regularUpdates: 'Düzenli Güncellemeler',
    regularUpdatesDesc: 'Fiyatlar her 10 dakikada bir güncellenir. Veriler gecikmiş olabilir.',
    indicativeOnly: 'Yalnızca Gösterge',
    indicativeOnlyDesc: 'Gösterilen fiyatlar gösterge niteliğindedir ve gerçek işlem fiyatlarından farklı olabilir.',
    visitSource: 'Kaynağı Ziyaret Et',
    dataDisclaimer: 'Veriler yalnızca bilgilendirme amaçlıdır. Herhangi bir yatırım kararı vermeden önce fiyatları doğrulayın.',
    
    // Login Page
    welcome: 'Hoş Geldiniz',
    signInWithEmail: 'E-postanızla giriş yapın',
    emailAddress: 'E-posta adresi',
    sendMagicLink: 'Sihirli Bağlantı Gönder',
    sendVerificationCode: 'Doğrulama Kodu Gönder',
    verificationCodeDescription: 'Size 6 haneli bir kod göndereceğiz. Şifre gerekmez!',
    enterVerificationCode: 'Doğrulama kodunu girin',
    codeSentTo: '6 haneli kodu şu adrese gönderdik:',
    verifyAndSignIn: 'Doğrula ve Giriş Yap',
    didntReceiveCode: 'Kodu almadınız mı?',
    resendCode: 'Tekrar gönder',
    resendIn: 'Tekrar gönder',
    invalidCode: 'Geçersiz kod. Lütfen tekrar deneyin.',
    enterFullCode: 'Lütfen 6 haneli kodu girin',
    failedToSendCode: 'Kod gönderilemedi. Lütfen tekrar deneyin.',
    back: 'Geri',
    magicLinkDescription: 'Size giriş yapmanız için bir bağlantı içeren bir e-posta göndereceğiz. Şifre gerekmez!',
    checkYourEmail: 'E-postanızı kontrol edin',
    magicLinkSent: 'Şu adrese bir giriş bağlantısı gönderdik:',
    linkExpiresIn: 'Giriş yapmak için e-postanızdaki bağlantıya tıklayın. Bağlantı 30 dakika içinde sona erer.',
    useDifferentEmail: 'Farklı bir e-posta kullan',
    backToHomepage: 'Ana sayfaya dön',
    bySigningIn: 'Giriş yaparak,',
    termsOfService: 'Hizmet Şartlarını',
    and: 've',
    privacyPolicy: 'Gizlilik Politikasını kabul etmiş olursunuz',
    
    // Verify Page
    verifyingLink: 'Bağlantınız doğrulanıyor',
    pleaseWait: 'Giriş yapılırken lütfen bekleyin...',
    youreSignedIn: 'Giriş yaptınız!',
    redirectingToDashboard: 'Kontrol panelinize yönlendiriliyorsunuz...',
    linkExpiredOrInvalid: 'Bağlantı süresi dolmuş veya geçersiz',
    requestNewLink: 'Yeni Bağlantı İste',
    
    // Footer
    footerDisclaimer: 'Bu bir yatırım tavsiyesi değildir. Altın fiyatları halka açık piyasa verilerine dayanmaktadır.',
    allRightsReserved: 'Tüm hakları saklıdır.',
    
    // Settings
    language: 'Dil',
    selectLanguage: 'Dil Seçin',
    
    // Delete confirmation
    deleteHolding: 'Varlığı Sil',
    deleteConfirmation: 'Altın varlığınızı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.',
    confirmDelete: 'Evet, Sil',
  },
  
  el: {
    // Common
    signIn: 'Σύνδεση',
    signOut: 'Αποσύνδεση',
    dashboard: 'Πίνακας Ελέγχου',
    settings: 'Ρυθμίσεις',
    loading: 'Φόρτωση...',
    grams: 'γραμμάρια',
    gram: 'γρ',
    reset: 'Επαναφορά',
    
    // Navigation
    whyGold: 'Γιατί Χρυσός',
    performance: 'Απόδοση',
    calculator: 'Υπολογιστής',
    sources: 'Πηγές',
    
    // Landing Page
    heroTitle: 'Με τα χρήματά μου,',
    heroHighlight: 'τι μπορώ να κάνω;',
    heroSubtitle: 'Εξερευνήστε πώς απέδωσε ο χρυσός τα τελευταία 5 χρόνια και εκτιμήστε μελλοντικά σενάρια χρησιμοποιώντας δημόσια δεδομένα αγοράς.',
    calculateWithGold: 'Υπολογισμός με Χρυσό',
    viewPerformance: 'Δείτε την 5ετή Απόδοση',
    publicMarketData: 'Δημόσια δεδομένα αγοράς',
    transparentSources: 'Διαφανείς πηγές',
    notFinancialAdvice: 'Δεν αποτελεί επενδυτική συμβουλή',
    liveMarketData: 'Ζωντανά δεδομένα αγοράς · Ενημέρωση κάθε 10 λεπτά',
    
    // Promo Card
    trackYourGold: 'Παρακολουθήστε τα Αποθέματα Χρυσού σας',
    promoDescription: 'Συνδεθείτε για να παρακολουθείτε το χαρτοφυλάκιο χρυσού σας.',
    dailyChanges: 'Καθημερινές Αλλαγές',
    
    // Dashboard
    welcomeBack: 'Καλώς ήρθατε πάλι,',
    hereIsPerformance: 'Δείτε πώς αποδίδει ο χρυσός σας',
    startTracking: 'Ξεκινήστε να παρακολουθείτε το χρυσό σας σήμερα',
    currentValue: 'Τρέχουσα Αξία',
    todaysChange: 'Σημερινή Αλλαγή',
    totalProfitLoss: 'Συνολικό Κέρδος/Ζημία',
    goldOwned: 'Χρυσός σε Κατοχή',
    yourGoldHolding: 'Το Απόθεμα Χρυσού σας',
    goldAmount: 'Ποσότητα Χρυσού',
    trackingSince: 'Παρακολούθηση από',
    daysTracked: 'Ημέρες παρακολούθησης',
    startPrice: 'Αρχική τιμή',
    sinceStart: 'από την αρχή',
    edit: 'Επεξεργασία',
    delete: 'Διαγραφή',
    perGram: 'ανά γραμμάριο',
    
    // Empty State
    startYourGoldJourney: 'Ξεκινήστε το Ταξίδι σας στον Χρυσό',
    emptyStateDescription: 'Εισάγετε πόσο χρυσό έχετε και δείτε πώς αποδίδει με ζωντανά δεδομένα αγοράς.',
    addYourGoldHolding: 'Προσθέστε το Απόθεμα Χρυσού σας',
    
    // Holding Modal
    addGoldHolding: 'Προσθήκη Αποθέματος Χρυσού',
    editGoldHolding: 'Επεξεργασία Αποθέματος Χρυσού',
    howMuchGold: 'Πόσο χρυσό έχετε;',
    gramsOfGold: 'γραμμάρια χρυσού',
    whenDidYouStart: 'Πότε ξεκινήσατε την παρακολούθηση;',
    selectDate: 'Επιλέξτε ημερομηνία',
    today: 'Σήμερα',
    save: 'Αποθήκευση',
    cancel: 'Ακύρωση',
    saving: 'Αποθήκευση...',
    
    // Why Gold Section
    whyGoldTitle: 'Γιατί Χρυσός;',
    whyGoldSubtitle: 'Ανακαλύψτε τα διαχρονικά οφέλη του χρυσού.',
    inflationHedge: 'Αντιστάθμιση Πληθωρισμού',
    inflationHedgeDesc: 'Ο χρυσός έχει ιστορικά διατηρήσει την αγοραστική του δύναμη.',
    globallyRecognized: 'Παγκόσμια Αναγνώριση',
    globallyRecognizedDesc: 'Αποδεκτός παγκοσμίως ως αποθεματικό αξίας.',
    portfolioDiversification: 'Διαφοροποίηση Χαρτοφυλακίου',
    portfolioDiversificationDesc: 'Συχνά κινείται ανεξάρτητα από μετοχές και ομόλογα.',
    highlyLiquid: 'Υψηλή Ρευστότητα',
    highlyLiquidDesc: 'Ένα από τα πιο ρευστά περιουσιακά στοιχεία παγκοσμίως.',
    
    // Calculator
    goldCalculator: 'Υπολογιστής Χρυσού',
    calculatorSubtitle: 'Δείτε πόσο χρυσό μπορείτε να αγοράσετε σήμερα.',
    investmentCalculator: 'Υπολογιστής Επένδυσης',
    calculateYourGold: 'Υπολογίστε την Επένδυσή σας σε Χρυσό',
    goldPricePerGram: 'Τιμή Χρυσού ανά Γραμμάριο',
    quickConverter: 'Γρήγορη Μετατροπή: Πόσο χρυσό μπορώ να αγοράσω;',
    enterAmount: 'Εισάγετε ποσό',
    enterAmountToConvert: 'Εισάγετε ποσό για μετατροπή',
    investmentAmount: 'Ποσό Επένδυσης',
    currency: 'Νόμισμα',
    timeHorizon: 'Χρονικός Ορίζοντας',
    years: 'χρόνια',
    year: 'έτος',
    scenario: 'Σενάριο',
    growthScenario: 'Σενάριο Ανάπτυξης',
    conservative: 'Συντηρητικό',
    moderate: 'Μέτριο',
    optimistic: 'Αισιόδοξο',
    base: 'Βασικό',
    calculate: 'Υπολογισμός',
    results: 'Αποτελέσματα',
    futureValue: 'Μελλοντική Αξία',
    goldGrams: 'Ποσότητα Χρυσού',
    totalReturn: 'Συνολική Απόδοση',
    annualReturn: 'Ετήσια Απόδοση',
    basedOnHistorical: 'Βασισμένο σε ιστορική απόδοση.',
    inputYourDetails: 'Εισάγετε τα Στοιχεία σας',
    readyToCalculate: 'Έτοιμο για Υπολογισμό',
    enterDetailsToSee: 'Εισάγετε τα στοιχεία επένδυσης για να δείτε τα αποτελέσματα.',
    yourGoldInvestment: 'Η Επένδυσή σας σε Χρυσό',
    todayYouCouldBuy: 'Σήμερα θα μπορούσατε να αγοράσετε περίπου',
    with: 'Με',
    afterYears: 'Μετά από',
    estimatedRange: 'Εκτιμώμενο Εύρος',
    selected: 'Επιλεγμένο',
    calculationDetails: 'Λεπτομέρειες Υπολογισμού',
    goldPrice: 'Τιμή Χρυσού',
    fxRate: 'Συναλλαγματική Ισοτιμία',
    annualGrowth: 'Ετήσια Ανάπτυξη',
    currentMarketData: 'Τρέχοντα Δεδομένα Αγοράς',
    loadingMarketData: 'Φόρτωση δεδομένων αγοράς...',
    indicative: 'Ενδεικτικό',
    liveRates: 'Ζωντανές τιμές',
    updatesEvery10Min: 'Ενημέρωση κάθε 10 λεπτά',
    disclaimer: 'Αυτό το εργαλείο παρέχει ενδεικτικά σενάρια. Δεν αποτελεί επενδυτική συμβουλή.',
    
    // Performance Section
    goldPerformance: 'Απόδοση Χρυσού',
    performanceSubtitle: 'Παρακολουθήστε πώς απέδωσε ο χρυσός τα τελευταία 5 χρόνια.',
    fiveYearPerformance: '5ετής Απόδοση',
    currentPrice: 'Τρέχουσα Τιμή',
    change24h: 'Αλλαγή 24ω',
    change1y: 'Αλλαγή 1 Έτους',
    change5y: 'Αλλαγή 5 Ετών',
    historicalPerformance: 'Ιστορική Απόδοση',
    goldPriceEUR: 'Τιμή Χρυσού (EUR)',
    trackGoldPerformance: 'Παρακολουθήστε την απόδοση του χρυσού σε ευρώ.',
    start: 'Αρχή',
    current: 'Τρέχουσα',
    change: 'Αλλαγή',
    loadingChartData: 'Φόρτωση δεδομένων γραφήματος...',
    dataTemporarilyUnavailable: 'Τα Δεδομένα Δεν Είναι Διαθέσιμα',
    historicalDataUnavailable: 'Τα ιστορικά δεδομένα τιμών δεν είναι διαθέσιμα. Δοκιμάστε αργότερα.',
    source: 'Πηγή',
    indicativeData: 'Ενδεικτικά δεδομένα',
    dataDelayedInfo: 'Τα δεδομένα μπορεί να καθυστερούν και παρέχονται μόνο για ενημέρωση.',
    
    // News Section
    goldMarketNews: 'Ειδήσεις Αγοράς Χρυσού',
    latestNews: 'Μείνετε ενημερωμένοι με τις τελευταίες ειδήσεις.',
    readMore: 'Διαβάστε περισσότερα',
    
    // Sources Section
    dataSources: 'Πηγές Δεδομένων',
    sourcesSubtitle: 'Χρησιμοποιούμε αξιόπιστες πηγές δεδομένων.',
    transparency: 'Διαφάνεια',
    howWeHandleData: 'Πώς Διαχειριζόμαστε τα Δεδομένα',
    publicSourcesOnly: 'Μόνο Δημόσιες Πηγές',
    publicSourcesDesc: 'Όλα τα δεδομένα προέρχονται από δημόσιες, αξιόπιστες πηγές.',
    regularUpdates: 'Τακτικές Ενημερώσεις',
    regularUpdatesDesc: 'Οι τιμές ανανεώνονται κάθε 10 λεπτά. Τα δεδομένα μπορεί να καθυστερούν.',
    indicativeOnly: 'Μόνο Ενδεικτικές',
    indicativeOnlyDesc: 'Οι τιμές είναι ενδεικτικές και μπορεί να διαφέρουν από τις πραγματικές.',
    visitSource: 'Επισκεφθείτε την Πηγή',
    dataDisclaimer: 'Τα δεδομένα παρέχονται μόνο για ενημέρωση. Επιβεβαιώστε τις τιμές πριν λάβετε αποφάσεις.',
    
    // Login Page
    welcome: 'Καλώς Ήρθατε',
    signInWithEmail: 'Συνδεθείτε με το email σας',
    emailAddress: 'Διεύθυνση email',
    sendMagicLink: 'Αποστολή Μαγικού Συνδέσμου',
    magicLinkDescription: 'Θα σας στείλουμε email με σύνδεσμο. Δεν χρειάζεται κωδικός!',
    checkYourEmail: 'Ελέγξτε το email σας',
    magicLinkSent: 'Στείλαμε σύνδεσμο στο',
    linkExpiresIn: 'Ο σύνδεσμος λήγει σε 30 λεπτά.',
    useDifferentEmail: 'Χρήση διαφορετικού email',
    backToHomepage: '← Επιστροφή στην αρχική',
    bySigningIn: 'Με τη σύνδεσή σας, συμφωνείτε με τους',
    termsOfService: 'Όρους Χρήσης',
    and: 'και την',
    privacyPolicy: 'Πολιτική Απορρήτου',
    
    // Verify Page
    verifyingLink: 'Επαλήθευση του συνδέσμου',
    pleaseWait: 'Παρακαλώ περιμένετε...',
    youreSignedIn: 'Συνδεθήκατε!',
    redirectingToDashboard: 'Ανακατεύθυνση...',
    linkExpiredOrInvalid: 'Ο σύνδεσμος έληξε ή δεν είναι έγκυρος',
    requestNewLink: 'Αίτηση Νέου Συνδέσμου',
    
    // Footer
    footerDisclaimer: 'Δεν αποτελεί επενδυτική συμβουλή.',
    allRightsReserved: 'Με επιφύλαξη παντός δικαιώματος.',
    
    // Settings
    language: 'Γλώσσα',
    selectLanguage: 'Επιλέξτε Γλώσσα',
    
    // Delete confirmation
    deleteHolding: 'Διαγραφή Αποθέματος',
    deleteConfirmation: 'Είστε βέβαιοι; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
    confirmDelete: 'Ναι, Διαγραφή',
  },
  
  ar: {
    // Common
    signIn: 'تسجيل الدخول',
    signOut: 'تسجيل الخروج',
    dashboard: 'لوحة التحكم',
    settings: 'الإعدادات',
    loading: 'جار التحميل...',
    grams: 'غرام',
    gram: 'غ',
    reset: 'إعادة تعيين',
    
    // Navigation
    whyGold: 'لماذا الذهب',
    performance: 'الأداء',
    calculator: 'الحاسبة',
    sources: 'المصادر',
    
    // Landing Page
    heroTitle: 'بأموالي،',
    heroHighlight: 'ماذا يمكنني أن أفعل؟',
    heroSubtitle: 'اكتشف أداء الذهب خلال السنوات الخمس الماضية وقدّر السيناريوهات المستقبلية باستخدام بيانات السوق العامة.',
    calculateWithGold: 'احسب بالذهب',
    viewPerformance: 'عرض أداء 5 سنوات',
    publicMarketData: 'بيانات السوق العامة',
    transparentSources: 'مصادر شفافة',
    notFinancialAdvice: 'ليست نصيحة مالية',
    liveMarketData: 'بيانات السوق المباشرة · تحديث كل 10 دقائق',
    
    // Promo Card
    trackYourGold: 'تتبع ممتلكاتك من الذهب',
    promoDescription: 'سجّل الدخول لمراقبة محفظتك من الذهب وتتبع أرباحك.',
    dailyChanges: 'التغييرات اليومية',
    
    // Dashboard
    welcomeBack: 'مرحباً بعودتك،',
    hereIsPerformance: 'إليك أداء ذهبك',
    startTracking: 'ابدأ تتبع رحلتك مع الذهب اليوم',
    currentValue: 'القيمة الحالية',
    todaysChange: 'تغيير اليوم',
    totalProfitLoss: 'إجمالي الربح/الخسارة',
    goldOwned: 'الذهب المملوك',
    yourGoldHolding: 'ممتلكاتك من الذهب',
    goldAmount: 'كمية الذهب',
    trackingSince: 'التتبع منذ',
    daysTracked: 'أيام التتبع',
    startPrice: 'سعر البداية',
    sinceStart: 'منذ البداية',
    edit: 'تعديل',
    delete: 'حذف',
    perGram: 'للغرام',
    
    // Empty State
    startYourGoldJourney: 'ابدأ رحلتك مع الذهب',
    emptyStateDescription: 'أدخل كمية الذهب التي تملكها وشاهد أداءها مع بيانات السوق المباشرة.',
    addYourGoldHolding: 'أضف ممتلكاتك من الذهب',
    
    // Holding Modal
    addGoldHolding: 'إضافة ممتلكات الذهب',
    editGoldHolding: 'تعديل ممتلكات الذهب',
    howMuchGold: 'كم من الذهب تملك؟',
    gramsOfGold: 'غرام من الذهب',
    whenDidYouStart: 'متى بدأت التتبع؟',
    selectDate: 'اختر التاريخ',
    today: 'اليوم',
    save: 'حفظ',
    cancel: 'إلغاء',
    saving: 'جار الحفظ...',
    
    // Why Gold Section
    whyGoldTitle: 'لماذا الذهب؟',
    whyGoldSubtitle: 'اكتشف الفوائد الخالدة للذهب كجزء من محفظة متنوعة.',
    inflationHedge: 'التحوط ضد التضخم',
    inflationHedgeDesc: 'حافظ الذهب تاريخياً على قوته الشرائية.',
    globallyRecognized: 'معترف به عالمياً',
    globallyRecognizedDesc: 'مقبول في جميع أنحاء العالم كمخزن للقيمة.',
    portfolioDiversification: 'تنويع المحفظة',
    portfolioDiversificationDesc: 'غالباً ما يتحرك بشكل مستقل عن الأسهم والسندات.',
    highlyLiquid: 'سيولة عالية',
    highlyLiquidDesc: 'أحد أكثر الأصول سيولة على مستوى العالم.',
    
    // Calculator
    goldCalculator: 'حاسبة الذهب',
    calculatorSubtitle: 'شاهد كم من الذهب يمكنك شراؤه اليوم وقدّر القيم المستقبلية.',
    investmentCalculator: 'حاسبة الاستثمار',
    calculateYourGold: 'احسب استثمارك في الذهب',
    goldPricePerGram: 'سعر الذهب للغرام',
    quickConverter: 'المحول السريع: كم من الذهب يمكنني شراؤه؟',
    enterAmount: 'أدخل المبلغ',
    enterAmountToConvert: 'أدخل المبلغ للتحويل',
    investmentAmount: 'مبلغ الاستثمار',
    currency: 'العملة',
    timeHorizon: 'الأفق الزمني',
    years: 'سنوات',
    year: 'سنة',
    scenario: 'السيناريو',
    growthScenario: 'سيناريو النمو',
    conservative: 'محافظ',
    moderate: 'معتدل',
    optimistic: 'متفائل',
    base: 'أساسي',
    calculate: 'احسب',
    results: 'النتائج',
    futureValue: 'القيمة المستقبلية',
    goldGrams: 'كمية الذهب',
    totalReturn: 'إجمالي العائد',
    annualReturn: 'العائد السنوي',
    basedOnHistorical: 'بناءً على الأداء التاريخي. النتائج السابقة لا تضمن العوائد المستقبلية.',
    inputYourDetails: 'أدخل بياناتك',
    readyToCalculate: 'جاهز للحساب',
    enterDetailsToSee: 'أدخل تفاصيل استثمارك لترى النتائج.',
    yourGoldInvestment: 'استثمارك في الذهب',
    todayYouCouldBuy: 'اليوم يمكنك شراء تقريباً',
    with: 'مع',
    afterYears: 'بعد',
    estimatedRange: 'النطاق المقدر',
    selected: 'المحدد',
    calculationDetails: 'تفاصيل الحساب',
    goldPrice: 'سعر الذهب',
    fxRate: 'سعر الصرف',
    annualGrowth: 'النمو السنوي',
    currentMarketData: 'بيانات السوق الحالية',
    loadingMarketData: 'جار تحميل بيانات السوق...',
    indicative: 'إرشادي',
    liveRates: 'أسعار مباشرة',
    updatesEvery10Min: 'تحديث كل 10 دقائق',
    disclaimer: 'هذه الأداة توفر سيناريوهات توضيحية. ليست نصيحة مالية.',
    
    // Performance Section
    goldPerformance: 'أداء الذهب',
    performanceSubtitle: 'تتبع أداء الذهب خلال السنوات الخمس الماضية.',
    fiveYearPerformance: 'أداء 5 سنوات',
    currentPrice: 'السعر الحالي',
    change24h: 'التغير خلال 24 ساعة',
    change1y: 'التغير خلال سنة',
    change5y: 'التغير خلال 5 سنوات',
    historicalPerformance: 'الأداء التاريخي',
    goldPriceEUR: 'سعر الذهب (يورو)',
    trackGoldPerformance: 'تتبع أداء الذهب باليورو عبر الزمن.',
    start: 'البداية',
    current: 'الحالي',
    change: 'التغير',
    loadingChartData: 'جار تحميل بيانات الرسم البياني...',
    dataTemporarilyUnavailable: 'البيانات غير متاحة مؤقتاً',
    historicalDataUnavailable: 'بيانات الأسعار التاريخية غير متاحة حالياً. يرجى المحاولة لاحقاً.',
    source: 'المصدر',
    indicativeData: 'بيانات إرشادية',
    dataDelayedInfo: 'قد تتأخر البيانات وهي مقدمة لأغراض إعلامية فقط.',
    
    // News Section
    goldMarketNews: 'أخبار سوق الذهب',
    latestNews: 'ابق على اطلاع بآخر الأخبار.',
    readMore: 'اقرأ المزيد',
    
    // Sources Section
    dataSources: 'مصادر البيانات',
    sourcesSubtitle: 'نستخدم مصادر بيانات موثوقة لجميع حساباتنا.',
    transparency: 'الشفافية',
    howWeHandleData: 'كيف نتعامل مع البيانات',
    publicSourcesOnly: 'مصادر عامة فقط',
    publicSourcesDesc: 'جميع البيانات تأتي من مصادر عامة وموثوقة.',
    regularUpdates: 'تحديثات منتظمة',
    regularUpdatesDesc: 'يتم تحديث الأسعار كل 10 دقائق. قد تتأخر البيانات.',
    indicativeOnly: 'إرشادية فقط',
    indicativeOnlyDesc: 'الأسعار المعروضة إرشادية وقد تختلف عن أسعار التداول الفعلية.',
    visitSource: 'زيارة المصدر',
    dataDisclaimer: 'البيانات مقدمة لأغراض إعلامية فقط. تحقق من الأسعار قبل اتخاذ أي قرارات استثمارية.',
    
    // Login Page
    welcome: 'مرحباً',
    signInWithEmail: 'سجّل الدخول بالبريد الإلكتروني',
    emailAddress: 'عنوان البريد الإلكتروني',
    sendMagicLink: 'إرسال الرابط السحري',
    magicLinkDescription: 'سنرسل لك رابط لتسجيل الدخول. لا حاجة لكلمة مرور!',
    checkYourEmail: 'تحقق من بريدك الإلكتروني',
    magicLinkSent: 'أرسلنا رابط إلى',
    linkExpiresIn: 'ينتهي الرابط خلال 30 دقيقة.',
    useDifferentEmail: 'استخدم بريداً مختلفاً',
    backToHomepage: 'العودة للصفحة الرئيسية ←',
    bySigningIn: 'بتسجيل الدخول، فإنك توافق على',
    termsOfService: 'شروط الخدمة',
    and: 'و',
    privacyPolicy: 'سياسة الخصوصية',
    
    // Verify Page
    verifyingLink: 'جار التحقق من الرابط',
    pleaseWait: 'يرجى الانتظار...',
    youreSignedIn: 'تم تسجيل دخولك!',
    redirectingToDashboard: 'جار التوجيه...',
    linkExpiredOrInvalid: 'الرابط منتهي أو غير صالح',
    requestNewLink: 'طلب رابط جديد',
    
    // Footer
    footerDisclaimer: 'ليست نصيحة مالية. أسعار الذهب مبنية على بيانات السوق العامة.',
    allRightsReserved: 'جميع الحقوق محفوظة.',
    
    // Settings
    language: 'اللغة',
    selectLanguage: 'اختر اللغة',
    
    // Delete confirmation
    deleteHolding: 'حذف الممتلكات',
    deleteConfirmation: 'هل أنت متأكد؟ لا يمكن التراجع عن هذا الإجراء.',
    confirmDelete: 'نعم، احذف',
  },
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLang, setCurrentLang] = useState(() => {
    const saved = localStorage.getItem('language');
    if (saved && languages[saved]) return saved;
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && languages[browserLang]) return browserLang;
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', currentLang);
    document.documentElement.dir = languages[currentLang]?.dir || 'ltr';
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  const t = (key) => {
    return translations[currentLang]?.[key] || translations['en']?.[key] || key;
  };

  const setLanguage = (lang) => {
    if (languages[lang]) {
      setCurrentLang(lang);
    }
  };

  const value = {
    currentLang,
    setLanguage,
    t,
    languages,
    isRTL: languages[currentLang]?.dir === 'rtl',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
